import React, { useMemo } from 'react';
import type { FormikProps } from 'formik';
import { FormControl, TextV2, SelectV1, InputV2, Spinner } from '@withjoy/joykit';
import { useAddressAutocompleteFieldController } from './AddressAutocompleteField.controller';
import { useTranslation } from '@shared/core';
import type { AddressDialogFields } from '@shared/types/Address.types';
import type { AddressUsedEnum } from './constants';
import { DISABLE_ADDRESS_VALIDATION_KEY } from './constants';
import { StyledSelectWrapper, styles } from './AddressAutocompleteField.styles';

type AddressAutocompleteFieldProps = Readonly<{
  label: string;
  caption: string;
  placeholderError: string;
  placeholder?: string;
  initialAddress1?: string;
  formik: FormikProps<AddressDialogFields>;
  formikKey: string;
  isNationalAddress: boolean;
  countryDropdownValue: string;
  clearAddressPressed: boolean;
  autoComplete?: string;
  setSuggestedAddress?: (value: AddressDialogFields | null) => void;
  onCallSuggestionApi?: (isNationalAddress: boolean) => void;
  setAddressUsed?: (addressUsed: AddressUsedEnum) => void;
  onDisableAddressSuggestions?: () => void;
}>;

export const AddressAutocompleteField: React.FC<AddressAutocompleteFieldProps> = props => {
  const { tq } = useTranslation('sharedComponents');
  const { addressNotFound, firstOptionAddress1Field, lastOptionAddress1Field, addressWithMoreThanOneSuggestion, required } = useMemo(
    () => ({
      addressNotFound: () => tq`Address not found`,
      firstOptionAddress1Field: () => tq`Keep typing to display more results`,
      lastOptionAddress1Field: () => tq`-- Add Address Manually --`,
      addressWithMoreThanOneSuggestion: ({ entries }: { entries: string | number }) => tq`Click for ${entries} more entries`,
      required: () => tq`Required`,
    }),
    [tq],
  );

  const {
    label,
    placeholder = '',
    placeholderError,
    caption,
    initialAddress1,
    formik,
    formikKey,
    isNationalAddress,
    countryDropdownValue,
    clearAddressPressed,
    autoComplete = 'none',
    setSuggestedAddress,
    onCallSuggestionApi,
    setAddressUsed,
    onDisableAddressSuggestions,
  } = props;
  const {
    error,
    isInvalid,
    address1FieldRef,
    showAddressLoading,
    showAddress1DropdownSuggestionList,
    isAddressValidationEnabled,
    addressSuggestionList,
    address1Dropdown,
    handleAddressSelection,
    handleAddressInputChange,
  } = useAddressAutocompleteFieldController({
    addressWithMoreThanOneSuggestion,
    firstOptionAddress1Field,
    lastOptionAddress1Field,
    required,
    placeholderError,
    initialAddress1,
    formik,
    formikKey,
    isNationalAddress,
    countryDropdownValue,
    clearAddressPressed,
    setSuggestedAddress,
    onCallSuggestionApi,
    setAddressUsed,
    onDisableAddressSuggestions,
  });

  const address1FieldOptions = addressSuggestionList.length
    ? addressSuggestionList
    : [
        {
          value: '-1',
          disabled: true,
          label: addressNotFound(),
        },
        {
          value: DISABLE_ADDRESS_VALIDATION_KEY,
          label: lastOptionAddress1Field(),
        },
      ];

  return (
    <FormControl
      label={label}
      caption={
        <TextV2
          typographyVariant="label2"
          color="mono10"
        >
          {caption}
        </TextV2>
      }
      isInvalid={isInvalid}
      error={error}
      isRequired
    >
      {isAddressValidationEnabled ? (
        <StyledSelectWrapper __css={styles.selectWrapper({ isVisible: showAddress1DropdownSuggestionList })}>
          <SelectV1
            ref={address1FieldRef}
            iconRight={<></>}
            optionFilter={() => true}
            closeMenuOnSelect={false}
            loading={showAddressLoading}
            options={address1FieldOptions}
            onChange={handleAddressSelection}
            onInputChange={handleAddressInputChange}
            value={address1Dropdown.label || address1Dropdown.value ? address1Dropdown : null}
            loadingMessage={() => <Spinner />}
            isInvalid={isInvalid}
            autoComplete={autoComplete}
            placeholder={placeholder}
          />
        </StyledSelectWrapper>
      ) : (
        <InputV2
          {...formik.getFieldProps(formikKey)}
          autoComplete={autoComplete}
          placeholder={placeholder}
        />
      )}
    </FormControl>
  );
};
